import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { AdminDashboardStats } from '../../types';

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<AdminDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    const response = await api.getAdminDashboard();
    if (response.success && response.data) {
      setStats(response.data);
    } else {
      setError(response.error || 'Failed to load dashboard');
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
          <p className="text-neutral-500 text-sm">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6 text-center">
        <p className="text-red-400">{error}</p>
        <button onClick={loadDashboard} className="mt-4 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors">
          Retry
        </button>
      </div>
    );
  }

  // Mock stats for demo (replace with actual API data)
  const mockStats: AdminDashboardStats = stats || {
    totalUsers: 156,
    totalResellers: 12,
    totalClients: 144,
    activeSubscriptions: 128,
    pendingApprovals: 5,
    revenueThisMonth: 485000,
    revenueLastMonth: 425000,
    newClientsThisWeek: 8,
    topResellers: [
      { id: '1', email: 'reseller1@example.com', companyName: 'Digital Solutions BD', activeClients: 45, totalEarnings: 125000 },
      { id: '2', email: 'reseller2@example.com', companyName: 'Tech Marketing Ltd', activeClients: 32, totalEarnings: 98000 },
      { id: '3', email: 'reseller3@example.com', companyName: 'Dhaka SEO Agency', activeClients: 28, totalEarnings: 87500 },
    ],
    recentActivity: []
  };

  const growthRate = ((mockStats.revenueThisMonth - mockStats.revenueLastMonth) / mockStats.revenueLastMonth * 100).toFixed(1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
          <p className="text-neutral-500">Overview of your platform performance</p>
        </div>
        <div className="flex gap-3">
          <button className="px-4 py-2 bg-[#1a1a1a] border border-[#262626] text-neutral-300 rounded-xl hover:bg-[#262626] transition-colors text-sm">
            📊 Export Report
          </button>
          <Link to="/admin/users" className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl transition-colors text-sm font-medium">
            + Add User
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Users */}
        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-2xl">👥</span>
            <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-[10px] font-bold uppercase rounded">
              All Users
            </span>
          </div>
          <p className="text-3xl font-bold text-white">{mockStats.totalUsers}</p>
          <p className="text-neutral-500 text-sm mt-1">
            {mockStats.totalResellers} Resellers • {mockStats.totalClients} Clients
          </p>
        </div>

        {/* Active Subscriptions */}
        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-2xl">💳</span>
            <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-[10px] font-bold uppercase rounded">
              Active
            </span>
          </div>
          <p className="text-3xl font-bold text-white">{mockStats.activeSubscriptions}</p>
          <p className="text-neutral-500 text-sm mt-1">
            {((mockStats.activeSubscriptions / mockStats.totalClients) * 100).toFixed(0)}% conversion rate
          </p>
        </div>

        {/* Pending Approvals */}
        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-2xl">⏳</span>
            <span className={`px-2 py-1 ${mockStats.pendingApprovals > 0 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-neutral-500/20 text-neutral-400'} text-[10px] font-bold uppercase rounded`}>
              Pending
            </span>
          </div>
          <p className="text-3xl font-bold text-white">{mockStats.pendingApprovals}</p>
          <Link to="/admin/users?status=pending" className="text-emerald-500 text-sm hover:text-emerald-400 transition-colors">
            Review pending →
          </Link>
        </div>

        {/* Revenue */}
        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-2xl">💰</span>
            <span className={`px-2 py-1 ${parseFloat(growthRate) >= 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'} text-[10px] font-bold uppercase rounded`}>
              {parseFloat(growthRate) >= 0 ? '+' : ''}{growthRate}%
            </span>
          </div>
          <p className="text-3xl font-bold text-white">৳{(mockStats.revenueThisMonth / 1000).toFixed(0)}K</p>
          <p className="text-neutral-500 text-sm mt-1">This month's revenue</p>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Resellers */}
        <div className="lg:col-span-2 bg-[#121212] border border-[#262626] rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-[#262626] flex items-center justify-between">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">Top Resellers</h3>
            <Link to="/admin/resellers" className="text-emerald-500 text-xs hover:text-emerald-400 transition-colors">
              View All →
            </Link>
          </div>
          <div className="divide-y divide-[#262626]">
            {mockStats.topResellers.map((reseller, index) => (
              <div key={reseller.id} className="p-4 hover:bg-[#1a1a1a] transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#1a1a1a] border border-[#262626] rounded-full flex items-center justify-center text-lg font-bold text-emerald-400">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-white truncate">{reseller.companyName}</p>
                    <p className="text-neutral-500 text-sm truncate">{reseller.email}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-white">{reseller.activeClients} clients</p>
                    <p className="text-emerald-500 text-sm">৳{(reseller.totalEarnings / 1000).toFixed(0)}K earned</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-[#121212] border border-[#262626] rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-[#262626]">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">Quick Actions</h3>
          </div>
          <div className="p-4 space-y-3">
            <Link
              to="/admin/users?status=pending"
              className="flex items-center gap-3 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-xl hover:bg-yellow-500/20 transition-colors group"
            >
              <span className="text-xl">⚡</span>
              <div className="flex-1">
                <p className="text-white font-medium group-hover:text-yellow-400 transition-colors">Pending Approvals</p>
                <p className="text-neutral-500 text-xs">{mockStats.pendingApprovals} accounts waiting</p>
              </div>
              <span className="text-neutral-500 group-hover:text-yellow-400 transition-colors">→</span>
            </Link>

            <Link
              to="/admin/plans"
              className="flex items-center gap-3 p-3 bg-[#1a1a1a] border border-[#262626] rounded-xl hover:bg-[#262626] transition-colors group"
            >
              <span className="text-xl">💳</span>
              <div className="flex-1">
                <p className="text-white font-medium">Manage Plans</p>
                <p className="text-neutral-500 text-xs">Configure subscription tiers</p>
              </div>
              <span className="text-neutral-500 group-hover:text-white transition-colors">→</span>
            </Link>

            <Link
              to="/admin/features"
              className="flex items-center gap-3 p-3 bg-[#1a1a1a] border border-[#262626] rounded-xl hover:bg-[#262626] transition-colors group"
            >
              <span className="text-xl">⚙️</span>
              <div className="flex-1">
                <p className="text-white font-medium">Feature Settings</p>
                <p className="text-neutral-500 text-xs">Toggle features per plan</p>
              </div>
              <span className="text-neutral-500 group-hover:text-white transition-colors">→</span>
            </Link>

            <Link
              to="/admin/commissions"
              className="flex items-center gap-3 p-3 bg-[#1a1a1a] border border-[#262626] rounded-xl hover:bg-[#262626] transition-colors group"
            >
              <span className="text-xl">💰</span>
              <div className="flex-1">
                <p className="text-white font-medium">Commission Payouts</p>
                <p className="text-neutral-500 text-xs">Process reseller payments</p>
              </div>
              <span className="text-neutral-500 group-hover:text-white transition-colors">→</span>
            </Link>

            <Link
              to="/admin/activity"
              className="flex items-center gap-3 p-3 bg-[#1a1a1a] border border-[#262626] rounded-xl hover:bg-[#262626] transition-colors group"
            >
              <span className="text-xl">📋</span>
              <div className="flex-1">
                <p className="text-white font-medium">Activity Logs</p>
                <p className="text-neutral-500 text-xs">Monitor user actions</p>
              </div>
              <span className="text-neutral-500 group-hover:text-white transition-colors">→</span>
            </Link>
          </div>
        </div>
      </div>

      {/* New This Week */}
      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
            <span className="text-2xl">📈</span>
          </div>
          <div>
            <h3 className="font-bold text-white">{mockStats.newClientsThisWeek} new clients this week!</h3>
            <p className="text-emerald-400 text-sm">Your platform is growing. Keep up the great work!</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
